import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

function Shell({ title, children, testid }) {
  return (
    <div data-universe="common" className="pagne flex min-h-screen items-center justify-center bg-paper p-4">
      <div className="card w-full max-w-md p-8" data-testid={testid}>
        <h1 className="mb-4 font-display text-3xl font-bold uppercase text-ink">{title}</h1>
        {children}
        <Link to="/admin/login" className="mt-6 inline-block text-sm text-u underline" data-testid="back-to-login">Retour à la connexion</Link>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  useSeo("Mot de passe oublié");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell title="Mot de passe oublié" testid="forgot-password-page">
      {done ? (
        <p className="rounded-md bg-u-soft p-4 text-sm" role="status" data-testid="forgot-password-success">
          Si un compte administrateur correspond à cette adresse, un e-mail contenant un lien de réinitialisation (valable 1 heure) vient d'être envoyé.
        </p>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form">
          <p className="mb-4 text-sm text-neutral-600">Saisissez l'e-mail de votre compte administrateur.</p>
          {error && <p className="mb-4 rounded-md bg-asso/10 p-3 text-sm text-asso" role="alert">{error}</p>}
          <label className="label" htmlFor="fp-email">E-mail</label>
          <input id="fp-email" type="email" required className="input mb-5" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email" />
          <button className="btn-u w-full" disabled={loading} data-testid="forgot-password-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Envoyer le lien
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  useSeo("Nouveau mot de passe");
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError("Les deux mots de passe ne correspondent pas.");
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Shell title="Nouveau mot de passe" testid="reset-password-page">
      {done ? (
        <div className="text-center" role="status" data-testid="reset-password-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-u" aria-hidden="true" />
          <p className="mt-3">Votre mot de passe a été modifié. Vous pouvez vous connecter.</p>
          <Link to="/admin/login" className="btn-u mt-5" data-testid="reset-password-login">Se connecter</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="reset-password-form">
          {error && <p className="mb-4 rounded-md bg-asso/10 p-3 text-sm text-asso" role="alert" data-testid="reset-password-error">{error}</p>}
          <label className="label" htmlFor="rp-1">Nouveau mot de passe (8 caractères min.)</label>
          <input id="rp-1" type="password" minLength={8} required className="input mb-4" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-new" />
          <label className="label" htmlFor="rp-2">Confirmer le mot de passe</label>
          <input id="rp-2" type="password" minLength={8} required className="input mb-5" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" />
          <button className="btn-u w-full" disabled={loading} data-testid="reset-password-submit">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </form>
      )}
    </Shell>
  );
}
